import axios from 'axios';

/**
 * Talks to our own /api/google-maps proxy, never to Google directly - the
 * Places API key stays on the server. The only value that reaches the browser
 * is the referrer-restricted Maps Embed key used for the map iframe.
 *
 * A 503 from the proxy means no key is configured, which callers should treat
 * as "autocomplete unavailable" and fall back to plain text entry.
 */
class ServiceGoogleMaps {
  browserKeyPromise = null;

  /**
   * A Places session token. Autocomplete requests sharing a token with the
   * Place Details call that ends the session are not billed, so callers should
   * keep one token for a whole typing session and call newSessionToken() again
   * once details have been fetched.
   *
   * crypto.randomUUID needs a secure context, so fall back the same way
   * signup-wizard-context does.
   */
  newSessionToken = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
      return hex;
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  autocomplete = async (input, sessionToken) => {
    try {
      const response = await axios.get(
        '/api/google-maps/autocomplete',
        { params: { input, sessionToken } }
      );
      return response?.data ?? [];
    } catch (error) {
      return [];
    }
  }

  placeDetails = async (placeId, sessionToken) => {
    try {
      const response = await axios.get(
        `/api/google-maps/place/${encodeURIComponent(placeId)}`,
        { params: { sessionToken } }
      );
      return response?.data ?? null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetched once per page load; resolves to '' when no key is configured.
   * A failed request is not cached - clearing the memo lets the next caller
   * retry, so one transient blip does not disable the map for the whole
   * session.
   */
  getBrowserKey = () => {
    if (!this.browserKeyPromise) {
      this.browserKeyPromise = axios
        .get('/api/google-maps/browser-key')
        .then(response => response?.data?.key || '')
        .catch(() => {
          this.browserKeyPromise = null;
          return '';
        });
    }
    return this.browserKeyPromise;
  }
}

export const ServiceGoogleMapsInstance = new ServiceGoogleMaps();
export default ServiceGoogleMapsInstance;
